import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  Upload,
  FileText,
  X,
  Loader2,
  Search,
  Link2,
  Check,
  Calendar,
  Eye,
  Sparkles,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useDocumentLink, type EntityType, type DocConfig, type ExistingDocument } from "@/hooks/useDocumentLink";
import { useDocumentPreview } from "@/contexts/DocumentPreviewContext";
import type { Tables } from "@/integrations/supabase/types";

// Re-export types for consumers
export type { DocConfig, EntityType };

export interface UnifiedDocumentLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: EntityType;
  entityId: string;
  docConfig: DocConfig;
  linkTableName: string;
  onSuccess?: () => void;
  /** Default active tab */
  defaultTab?: "link" | "upload";
  /** Show validity date fields */
  showValidityDates?: boolean;
  /** Custom header content rendered above tabs */
  customHeader?: React.ReactNode;
  /** Family member ID for linking documents to a specific family member */
  familyMemberId?: string;
}

export function UnifiedDocumentLinkDialog({
  open,
  onOpenChange,
  entityType,
  entityId,
  docConfig,
  linkTableName,
  onSuccess,
  defaultTab = "link",
  showValidityDates = true,
  customHeader,
  familyMemberId,
}: UnifiedDocumentLinkDialogProps) {
  const { openDocument } = useDocumentPreview();
  const [activeTab, setActiveTab] = useState<"link" | "upload">(defaultTab);

  const {
    searchQuery,
    setSearchQuery,
    selectedDoc,
    setSelectedDoc,
    file,
    setFile,
    expiresAt,
    setExpiresAt,
    validFrom,
    setValidFrom,
    documents,
    loadingDocs,
    handleLink,
    handleUpload,
    clearFile,
    isLinking,
    isUploading,
    isExtractingDates,
    datesExtracted,
    isExtractingLinkDates,
    linkDatesExtracted,
  } = useDocumentLink({
    entityType,
    entityId,
    docConfig,
    linkTableName,
    open,
    onSuccess: () => {
      onOpenChange(false);
      onSuccess?.();
    },
    familyMemberId,
  });

  const handlePreview = useCallback((doc: ExistingDocument) => {
    // Cast to Tables<"documents"> for the preview sheet
    openDocument(doc as unknown as Tables<"documents">);
  }, [openDocument]);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        setFile(acceptedFiles[0]);
      }
    },
    [setFile]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".png", ".jpg", ".jpeg", ".heic", ".webp"],
    },
    maxFiles: 1,
    maxSize: 20 * 1024 * 1024,
  });

  const handleClose = (newOpen: boolean) => {
    if (!isLinking && !isUploading) {
      onOpenChange(newOpen);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            {docConfig.label}
          </DialogTitle>
          <DialogDescription>
            Sélectionnez un document existant ou importez-en un nouveau
          </DialogDescription>
        </DialogHeader>

        {customHeader}

        <Tabs defaultValue={defaultTab} value={activeTab} onValueChange={(v) => setActiveTab(v as "link" | "upload")}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="link" className="gap-2">
              <Link2 className="w-4 h-4" />
              Lier un document
            </TabsTrigger>
            <TabsTrigger value="upload" className="gap-2">
              <Upload className="w-4 h-4" />
              Importer
            </TabsTrigger>
          </TabsList>

          {/* Link Existing Tab */}
          <TabsContent value="link" className="space-y-4 mt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher dans vos documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            <ScrollArea className="h-[250px] border rounded-lg">
              {loadingDocs ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : documents.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground text-sm p-4">
                  <FileText className="w-8 h-8 mb-2 opacity-50" />
                  {searchQuery ? "Aucun document trouvé" : "Aucun document disponible"}
                  <p className="text-xs mt-2 text-center">
                    Vous pouvez importer un nouveau document directement
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 gap-2"
                    onClick={() => setActiveTab("upload")}
                  >
                    <Upload className="w-4 h-4" />
                    Importer un document
                  </Button>
                </div>
              ) : (
                <div className="p-2 space-y-1">
                  {documents.map((doc) => (
                    <button
                      key={doc.id}
                      type="button"
                      onClick={() => setSelectedDoc(doc)}
                      className={cn(
                        "w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors",
                        selectedDoc?.id === doc.id
                          ? "bg-primary/10 border border-primary"
                          : "hover:bg-muted border border-transparent"
                      )}
                    >
                      <FileText className="w-5 h-5 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{doc.title}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {doc.file_name} •{" "}
                          {format(new Date(doc.created_at), "dd MMM yyyy", { locale: fr })}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePreview(doc);
                        }}
                        className="p-1.5 rounded-md hover:bg-muted-foreground/10 text-muted-foreground hover:text-foreground transition-colors"
                        title="Prévisualiser"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {selectedDoc?.id === doc.id && (
                        <Check className="w-5 h-5 text-primary shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>

            {/* AI Extraction Status (existing doc link) */}
            {selectedDoc && isExtractingLinkDates && (
              <div className="flex items-center gap-2 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
                <span className="text-sm text-primary font-medium">
                  Analyse IA des dates en cours...
                </span>
              </div>
            )}

            {selectedDoc && linkDatesExtracted && !isExtractingLinkDates && (expiresAt || validFrom) && (
              <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <Sparkles className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-700 font-medium">
                  Dates pré-remplies automatiquement
                </span>
              </div>
            )}

            {/* Validity dates for link */}
            {showValidityDates && selectedDoc && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="linkValidFrom" className="text-xs flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Valide à partir de
                    {isExtractingLinkDates && (
                      <Loader2 className="w-3 h-3 animate-spin text-muted-foreground ml-1" />
                    )}
                  </Label>
                  <Input
                    id="linkValidFrom"
                    type="date"
                    value={validFrom}
                    onChange={(e) => setValidFrom(e.target.value)}
                    className="text-sm"
                    disabled={isExtractingLinkDates}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="linkExpiresAt" className="text-xs flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Date d'expiration
                    {isExtractingLinkDates && (
                      <Loader2 className="w-3 h-3 animate-spin text-muted-foreground ml-1" />
                    )}
                  </Label>
                  <Input
                    id="linkExpiresAt"
                    type="date"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    className="text-sm"
                    disabled={isExtractingLinkDates}
                  />
                </div>
              </div>
            )}

            <Button
              className="w-full"
              onClick={handleLink}
              disabled={!selectedDoc || isLinking || isExtractingLinkDates}
            >
              {isLinking ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Liaison en cours...
                </>
              ) : isExtractingLinkDates ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyse en cours...
                </>
              ) : (
                <>
                  <Link2 className="w-4 h-4 mr-2" />
                  Lier ce document
                </>
              )}
            </Button>
          </TabsContent>

          {/* Upload Tab */}
          <TabsContent value="upload" className="space-y-4 mt-4">
            <div
              {...getRootProps()}
              className={cn(
                "border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors",
                isDragActive
                  ? "border-primary bg-primary/5"
                  : "border-muted-foreground/25 hover:border-primary/50",
                file && "border-primary bg-primary/5"
              )}
            >
              <input {...getInputProps()} />
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="w-8 h-8 text-primary" />
                  <div className="text-left">
                    <p className="font-medium truncate max-w-[200px]">{file.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} Ko
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearFile();
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {isDragActive
                      ? "Déposez le fichier ici..."
                      : "Glissez-déposez ou cliquez pour sélectionner"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    PDF ou image, max 20 Mo
                  </p>
                </>
              )}
            </div>

            {/* AI Extraction Status */}
            {file && isExtractingDates && (
              <div className="flex items-center gap-2 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
                <span className="text-sm text-primary font-medium">
                  Analyse IA des dates en cours...
                </span>
              </div>
            )}

            {file && datesExtracted && !isExtractingDates && (
              <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <Sparkles className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-700 font-medium">
                  Dates extraites automatiquement
                </span>
              </div>
            )}

            {/* Validity dates for upload */}
            {showValidityDates && file && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="uploadValidFrom" className="text-xs flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Valide à partir de
                    {isExtractingDates && (
                      <Loader2 className="w-3 h-3 animate-spin text-muted-foreground ml-1" />
                    )}
                  </Label>
                  <Input
                    id="uploadValidFrom"
                    type="date"
                    value={validFrom}
                    onChange={(e) => setValidFrom(e.target.value)}
                    className="text-sm"
                    disabled={isExtractingDates}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="uploadExpiresAt" className="text-xs flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Date d'expiration
                    {isExtractingDates && (
                      <Loader2 className="w-3 h-3 animate-spin text-muted-foreground ml-1" />
                    )}
                  </Label>
                  <Input
                    id="uploadExpiresAt"
                    type="date"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    className="text-sm"
                    disabled={isExtractingDates}
                  />
                </div>
              </div>
            )}

            <Button
              className="w-full"
              onClick={handleUpload}
              disabled={!file || isUploading || isExtractingDates}
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : isExtractingDates ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Analyse en cours...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Importer et lier
                </>
              )}
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
